import React, { useContext, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { regenerateUrlWithQuery } from '../../../utils/UrlUtil';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import ReportGenForm from './components/ReportGenForm';
import TeamEdit from './components/ManagementTeamEdit/TeamEditContainer';
import { Loading } from '../../../components/Loading';
import EditExecInforContainer from './components/EditInfor/EditExecInforContainer';
import { updateExecutiveReport, deleteExecutiveReport } from '../../../services/apps/ExecutiveReportService';
import ConfirmModal from './ExecReportDeleteConfirmModal';
import { DeleteTextButton } from '../../../components/button/ActionButton';
import ExecutiveReportSurveyListContainer from './survey-list/ExecutiveReportSurveyListContainer';
import { useHistory, useParams } from 'react-router-dom';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

const messages = defineMessages({
  plhFile: {
    id: 'res_import.nothing_selected',
    defaultMessage: 'Nothing selected',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  deleteExecutiveReport: { id: 'del_header.executive_report', defaultMessage: 'Delete Executive Report' },
  editTeam: { id: 'executive_report.edit-team', defaultMessage: 'Add/Remove Managers' },
  edit: { id: 'actions.edit', defaultMessage: 'Edit' },
});

const EXEC_DETAIL_REPORT = gql`
  query reportDetail($accountId: ID!, $id: ID!) {
    account(id: $accountId) {
      id
      executiveReport(id: $id) {
        id
        teamName
        numberOfSurveys
        createdOn
        updatedOn
        template {
          id
          title
        }
      }
    }
  }
`;

const ExecReportDetail = () => {
  const history = useHistory();
  const { reportId } = useParams();
  const { formatMessage } = useIntl();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const DEFAULT_ACCOUNT_STATE = {
    id: null,
    executiveReport: {
      id: null,
      teamName: null,
      numberOfSurveys: 0,
      createdOn: null,
      updatedOn: null,
      template: {
        id: null,
        title: null,
      },
    },
  };

  const [mngModal, setManageModal] = useState(false);
  const [infoEditing, setInfoEditing] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const { loading, data: queryData } = useQuery(EXEC_DETAIL_REPORT, {
    variables: { accountId: currentAccount, id: reportId },
  });

  useEffect(() => {
    if (queryData) {
      setAccount(queryData.account);
    }
  }, [queryData]);

  const remountTeamList = () => {
    const oldPath = history.location;
    const newPath = regenerateUrlWithQuery(oldPath.pathname, {});
    history.replace(newPath);
  };

  const onUpdate = async (value) => {
    try {
      await updateExecutiveReport({
        accountId: currentAccount,
        executiveReportId: reportId,
        teamName: value.managerTeamName,
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const processDeleting = async () => {
    if (reportId) {
      try {
        await deleteExecutiveReport({
          accountId: currentAccount,
          executiveReportId: reportId,
        });
        const executiveReportsURL = `/accounts/${currentAccount}/executive-reports`;
        growlActionSuccess(growl, formatMessage(messages.successNoti));
        history.push(executiveReportsURL);
        // window.location.reload();
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }
    setShowDeleteConfirmModal(false);
  };

  if (loading || !account || !account.executiveReport) {
    return <Loading />;
  }

  const { executiveReport } = account;
  const template = executiveReport && executiveReport.template ? executiveReport.template.title : '';
  const templateId = executiveReport && executiveReport.template ? executiveReport.template.id : null;
  const exeReportId = executiveReport ? executiveReport.id : null;

  const editFormInit = {
    detail: {
      name: executiveReport ? executiveReport.name : '',
      managerTeamName: executiveReport ? executiveReport.teamName : '',
    },
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <div className="p-col-12 p-sm-12 p-md-6">
              {infoEditing ? (
                <div className="wrapper">
                  <EditExecInforContainer
                    initialValues={editFormInit}
                    onCancel={() => setInfoEditing(false)}
                    onUpdate={onUpdate}
                    executiveReport={executiveReport}
                  />
                </div>
              ) : (
                <Panel
                  header={
                    <div className="p-grid p-nogutter">
                      <div className="p-col">{`${executiveReport.teamName} - ${template}`}</div>
                      <div className="p-col-2 align-right">
                        <Button
                          type="button"
                          icon="fa fa-pencil"
                          className="p-button-secondary"
                          onClick={() => setInfoEditing(true)}
                          disabled={subscriptionIsExpired}
                          tooltip={formatMessage(messages.edit)}
                          tooltipOptions={{ position: 'bottom' }}
                        />
                      </div>
                    </div>
                  }
                >
                  <div className="p-grid">
                    <div className="p-col">
                      <Button
                        label={formatMessage(messages.editTeam)}
                        type="button"
                        icon="fa fa-plus-circle"
                        disabled={subscriptionIsExpired}
                        onClick={() => setManageModal(true)}
                      />
                    </div>
                    <div className="p-col align-right">
                      <DeleteTextButton
                        position="right"
                        onClick={() => setShowDeleteConfirmModal(true)}
                        disabled={subscriptionIsExpired}
                        text={formatMessage(messages.deleteExecutiveReport)}
                      />
                    </div>
                  </div>
                  <ReportGenForm exeReportId={exeReportId} />
                </Panel>
              )}

              <TeamEdit
                modalName={`${executiveReport.teamName}`}
                modalOpen={mngModal}
                hideModal={() => setManageModal(false)}
                template={templateId}
                remount={remountTeamList}
              />
              <ConfirmModal
                modalOpen={showDeleteConfirmModal}
                reject={() => setShowDeleteConfirmModal(false)}
                confirm={processDeleting}
              />
            </div>
            <div className="p-col-12">
              <Panel>
                <ExecutiveReportSurveyListContainer />
              </Panel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecReportDetail;
